import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { sendNotification } from 'utils/helperMethods';
import { useEditWriteGuard } from '../../../EditSession/useEditWriteGuard';

export interface KeepFlagsPayload {
    promoId: string;
    keepDates?: boolean;
    keepPromo?: boolean;
}

interface KeepFlagsRequestBody {
    keep_dates?: boolean;
    keep_promo?: boolean;
    user_id: string;
}

interface KeepFlagsResponse {
    promotion_id: string;
    keep_dates: boolean;
    keep_promo: boolean;
    status: string;
    message: string;
}

const setKeepFlags = async (promotionId: string, body: KeepFlagsRequestBody): Promise<KeepFlagsResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.keepDatesAndPromos(promotionId), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
        data: body,
    });
    return response.data;
};

const useKeepFlagsMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    const queryClient = useQueryClient();
    const assertCanWrite = useEditWriteGuard();

    const { mutate, isPending } = useMutation({
        mutationFn: ({ promoId, keepDates, keepPromo }: KeepFlagsPayload) => {
            assertCanWrite();
            return setKeepFlags(promoId, {
                ...(keepDates === undefined ? {} : { keep_dates: keepDates }),
                ...(keepPromo === undefined ? {} : { keep_promo: keepPromo }),
                user_id: localStorage.getItem('email') ?? '',
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendarData'] });
            sendNotification('success', 'Promo successfully updated');
            onSuccess?.();
        },
        onError: (error: any) => {
            const message =
                error?.response?.data?.detail ||
                error?.response?.data?.message ||
                error?.message ||
                'Failed to update promo';
            sendNotification('error', message);
        },
    });

    return { mutate, isPending };
};

export default useKeepFlagsMutation;
